import { useCallback, useEffect, useState } from 'react';

/**
 * The visitor's language choice, remembered.
 *
 * /docs and /support each used to start at English on every mount, so picking
 * VI on one page and moving to the other — or simply reloading — threw the
 * choice away. It lives in localStorage rather than in a provider because it
 * has to survive a reload, and because the two pages are separate lazy chunks
 * that never share a React tree.
 */

const STORAGE_KEY = 'owla.locale';
const SUPPORTED = new Set(['en', 'vi']);

/** Storage throws in private mode and when site data is blocked; a missing choice is not an error. */
function readStored() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED.has(stored) ? stored : null;
  } catch {
    return null;
  }
}

export default function useLocale(fallback = 'en') {
  const [locale, setLocaleState] = useState(() => readStored() ?? fallback);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED.has(next)) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisted, but the page still switches for this visit.
    }
  }, []);

  // Screen readers pick their voice from this, and so does the browser's
  // offer to translate the page.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // `storage` fires in the *other* tabs, so a second tab left open does not sit
  // in the language the visitor just moved away from.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      if (SUPPORTED.has(event.newValue)) setLocaleState(event.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return [locale, setLocale];
}
